// import { height } from "@mui/system";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Image, Col, Form, Row, Table, Dropdown, Modal, Badge, Nav, Pagination } from "react-bootstrap";
import FeatherIcon from "feather-icons-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { logoutUser } from "../../redux/actions";
import AuthModal from "../../components/Modal";
import { Alert } from "react-bootstrap";
import api from "../../utils/ApiMethod";
import img from "../../assets/images/new.gif";
import { ADMIN_LOGIN_BASE_URL } from "../../apiservices/apiService";

// Sidebar menus an admin can grant/revoke per user - keys must match
// MENU_ACCESS_KEYS in backend-bssa-school-games/model/userModel.js.
const MENU_ACCESS_OPTIONS: { key: string; label: string }[] = [
  { key: "dashboards", label: "Dashboards" },
  { key: "schoolLevel", label: "School Level" },
  { key: "blockLevel", label: "Block Level" },
  { key: "districtLevel", label: "District Level" },
  { key: "stateLevel", label: "State Level" },
  { key: "allQuery", label: "All Query" },
  { key: "users", label: "Users" },
  { key: "crc", label: "CRC" },
  { key: "school", label: "School" },
  { key: "queryResolved", label: "Query Resolved" },
  { key: "mis", label: "MIS Report" },
];

const emptyMenuAccess = (): Record<string, boolean> =>
  MENU_ACCESS_OPTIONS.reduce((acc, { key }) => {
    acc[key] = false;
    return acc;
  }, {} as Record<string, boolean>);

// Users page tabs -> `category` query param sent to
// GET /get/all/registered/user (backend maps each to a `level` filter - see
// USER_CATEGORY_FILTERS in adminController.js).
const USER_CATEGORIES: { key: string; label: string }[] = [
  { key: "admin", label: "Admin" },
  { key: "crc", label: "CRC" },
  { key: "block", label: "Block" },
  { key: "district", label: "District" },
  { key: "state", label: "State" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;

// Renders a cheap CSS-only initial avatar instead of loading a profile
// image per row, so the table never triggers extra network/image decode
// work for users who mostly don't have one anyway.
const NameAvatar = ({ name }: { name?: string }) => (
  <div
    className="avatar-sm rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary fw-bold flex-shrink-0"
    style={{ width: 32, height: 32, fontSize: 13 }}
  >
    {(name || "?").trim().charAt(0).toUpperCase()}
  </div>
);

const APPROVAL_BADGE_VARIANT: Record<string, string> = {
  Approved: "success",
  Pending: "warning",
  Rejected: "danger",
};

const ApprovalBadge = ({ status }: { status?: string }) => (
  <Badge bg={APPROVAL_BADGE_VARIANT[status || ""] || "secondary"}>
    {status || "—"}
  </Badge>
);

// `region` comes back populated ({ _id, region_name }) from the API but is
// still a bare id string while a form is mid-edit - these two helpers let
// callers treat it uniformly either way.
const getRegionId = (region?: RegionRef): string =>
  typeof region === "object" && region ? region._id : region || "";
const getRegionName = (region?: RegionRef): string | undefined =>
  typeof region === "object" && region ? region.region_name : undefined;

// One label/value pair in the View User modal - keeps that layout terse.
const DetailField = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Col xs={12} md={6} className="mb-3">
    <div className="text-muted small">{label}</div>
    <div className="fw-medium">{value || "—"}</div>
  </Col>
);

interface OrgtnalSruItem {
  _id?: string;
  aboutText?: string;
  isDeleted?: boolean;
}

// The API sends `region` populated (`{ _id, region_name }`) on the users
// list/detail endpoints, but the create/update forms still just work with
// the bare id string while a region is being picked.
type RegionRef = string | { _id: string; region_name?: string };

interface userData {
  name?: string;
  email?: string;
  punchingid?: string;
  password?: string;
  hobbies?: string;
  role?: string;
  isDeleted?: boolean;
  imageUrl?: string;
  _id?: string;
  mob?: string;
  level?: string;
  region?: RegionRef;
  designation?: string;
  department?: string;
  menuAccess?: Record<string, boolean>;
  assignRegion?: RegionRef[];
}

interface OrgtnalStru {
  _id: string;
  name?: string;
  bannerUrl?: string;
  mobileBannerUrl?: string;
  aboutText?: string;
  // OrgtnalSruItem:array;
  isDeleted: boolean;
  email?: string;
  hobbies?: string;
  role?: string;
  punchingid?: string;
  designation?: string;
  superDuperAdminApprovalStatus?: string;
  superAdminApprovalStatus?: string;
  adminApprovalStatus?: string;
  innerData?: userData[] | [];
  department?: string;
  title?: string;
  mob?: string;
  level?: string;
  region?: RegionRef;
  Block_Name_Code?: string;
  CRC_Name_Code?: string;
  districtCode?: string;
  stateName?: string;
  createdBy?: string;
  createdAt?: string;
  menuAccess?: Record<string, boolean>;
}

interface UpdateServiceSectionProps {
  itemId: string; // Replace 'boolean' with the appropriate type if needed
  parentId?: string;
  innerdata: userData[];
  onCancel?: () => void;
}
interface ParentId {
  itemId?: string; // Replace 'boolean' with the appropriate type if needed
  id?: string;
  parentId?: string;
}
interface id {
  id?: string;
}

// -------------------------------------  for organizational Structured Item  ---------------
// ---999999999999999999 for update item
const UpdateEmployee: React.FC<UpdateServiceSectionProps> = ({
  itemId,
  parentId,
  innerdata,
  onCancel,
}) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [serviceSection, setServiceSection] = useState<OrgtnalStru[]>([]);
  const [aboutText, setAboutText] = useState("");
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [allRegions, setAllRegions] = useState<any>();
  const [level, setLevel] = useState("");
  const [mob, setMob] = useState("");
  const [region, setRegion] = useState("");
  const [regionName, setRegionName] = useState("");
  const [email, setEmail] = useState("");
  const [punchingid, setPunchingID] = useState("");
  const [designation, setDesignation] = useState("");
  const [menuAccess, setMenuAccess] = useState<Record<string, boolean>>(emptyMenuAccess());
  const [assignRegion, setAssignRegion] = useState<string[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isResponse, setIsResponse] = useState("");
  const [url, setUrl] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const Level = ["Cluster", "Block", "District", "State", "Admin"];
  const allOptions = ["Sports", "Physical Teacher"];

  const fetchAllRegions = async () => {
    try {
      const response = await api.fetchData("/api/regions");
      setAllRegions(response?.data?.regions);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAllRegions();
  }, []);
  // ------------------------- for saving the data to updte
  const { user, userLoggedIn, loading, error, userId } = useSelector(
    (state: RootState) => ({
      user: state.Auth.user,
      loading: state.Auth.loading,
      error: state.Auth.error,
      userLoggedIn: state.Auth.userLoggedIn,
      userId: state.Auth._id,
    })
  );
  let profileId = user._id ? user._id : user.user ? user.user._id : "";
  const Key_Key = user.moniterd_ttl
    ? user.moniterd_ttl
    : user.user
      ? user.user.moniterd_ttl
      : "";
  let profileName = user.name ? user.name : user.user.name;

  useEffect(() => {
    const fetchData = async () => {
      try {
        let admin =
          Role == "Cricket"
            ? `/user/${profileId}/${Key_Key}`
            : `/user/${profileId}/${Key_Key}/${Departmentforfilter}`;
        const response = await api.fetchDataAuth(
          // `/user/${profileId}/${Key_Key}/${Departmentforfilter}`
          `${admin}`
        );
        if (response.status === 400) {
          setIsError(true);
          let errorMessage = await response.message;
          setIsResponse(errorMessage);
        }

        if (response.status === 440) {
          alert("Session Expired");
          dispatch(logoutUser());
          window.location.href = ADMIN_LOGIN_BASE_URL; // Use the full URL, including the protocol (http or https)
        }
        if (!response.status == true) {
          let errorMsg = await response.json();
          setIsResponse(errorMsg.message);
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.data;
        setServiceSection(data);
      } catch (error) {
        console.error("Error during fetch:", error);
      }
    };
    fetchData();
  }, [profileId, Key_Key]);


  useEffect(() => {
    if (innerdata && innerdata.length > 0) {
      const filterItem = innerdata.find((item) => item._id === itemId);
      if (filterItem) {
        setName(filterItem.name || "");
        setDepartment(filterItem.department || "");
        setLevel(filterItem.level || "");
        setMob(filterItem.mob || "");
        setEmail(filterItem.email || "");
        setRegion(getRegionId(filterItem.region));
        setRegionName(getRegionName(filterItem.region) || "");
        setPunchingID(filterItem.punchingid || "");
        setDesignation(filterItem.designation || "");
        setMenuAccess({ ...emptyMenuAccess(), ...(filterItem.menuAccess || {}) });
        setAssignRegion(
          Array.isArray(filterItem.assignRegion)
            ? filterItem.assignRegion.map((r: any) => getRegionId(r))
            : []
        );
      }
    }
  }, [innerdata, itemId]);

  const toggleMenuAccess = (key: string) =>
    setMenuAccess((prev) => ({ ...prev, [key]: !prev[key] }));

  // Menu Access only applies to Admin-level users - clear any selection made
  // before switching away from Admin so a hidden, stale grant can't be
  // submitted along with the form.
  useEffect(() => {
    if (level !== "Admin") setMenuAccess(emptyMenuAccess());
  }, [level]);

  // assignRegion only makes sense alongside districtLevel/blockLevel menu
  // access - clear any hidden, stale selection once neither is checked.
  useEffect(() => {
    if (!menuAccess.districtLevel && !menuAccess.blockLevel) {
      setAssignRegion([]);
    }
  }, [menuAccess.districtLevel, menuAccess.blockLevel]);

  const assignRegionType = menuAccess.districtLevel
    ? "district"
    : menuAccess.blockLevel
      ? "block"
      : null;
  const assignRegionOptions = (allRegions || []).filter(
    (r: any) => r.region_type === assignRegionType
  );


  const handleSubmit = async (event: any) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
  };

  //   ------------------------------------------ setting image in the input--------------------------------
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const UpdateOrgStruItem = async (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault();
      const formData = new FormData();
      formData.append("name", name);
      formData.append("department", department);
      formData.append("level", level);
      formData.append("mob", mob);
      formData.append("email", email);
      formData.append("region", region);
      formData.append("punchingid", punchingid);
      formData.append("designation", designation);
      formData.append("modifiedBy", profileName);
      formData.append("menuAccess", JSON.stringify(menuAccess));
      formData.append("assignRegion", JSON.stringify(assignRegion));
      if (image) {
        formData.append("image", image);
      }
      try {
        const response = await api.updatePatchData(
          `/api/update/registered/user/${profileId}/employee/${itemId}/${Key_Key}`,
          formData
        );
        if (response.status === 440) {
          alert("Session Expired");
          dispatch(logoutUser());
          window.location.href = ADMIN_LOGIN_BASE_URL; // Use the full URL, including the protocol (http or https)
        }

        if (response.status === 429) {
          setShowModal(true);
        }
        if (response.status === 400) {
          setIsError(true);
          let errorMessage = await response.json();
          setIsResponse(errorMessage.message);
        }

        if (!response.status == true) {
          let errorMsg = await response.json();
          setIsResponse(errorMsg.message);
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        if (response.status == 200 || response.status == 201) {
          alert("Data updated");
          window.location.reload();
        }
      } catch (error: any) {
        console.error("Error during edit the banner:", error);
      }
    }
    setValidated(true);
  };
  let Role = user.hobbies ? user.hobbies : user.user.hobbies;

  let Departmentforfilter = user.department
    ? user.department
    : user.user.department;

  const handleCancel = () => {
    if (onCancel) onCancel();
    else window.location.reload();
  };

  return (
    <>
      {(innerdata || [])
        .filter((item) => item._id === itemId)
        .map((filterItem, index) => (
          <Form
            onSubmit={UpdateOrgStruItem}
            encType="multipart/form-data"
            key={index}
          >
            {isError && (
              <Alert variant="danger" className="my-2">
                {isResponse}
              </Alert>
            )}
            {showModal && <AuthModal />}

            <h6 className="text-uppercase text-muted fw-bold small mb-3">
              Basic Information
            </h6>
            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  required
                  onChange={(e) => setName(e.target.value)}
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Form.Label>
                  Email address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  autoComplete="off"
                  required
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Form.Label>
                  Mobile Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={mob}
                  pattern="[0-9]*"
                  title="Please enter only digits"
                  required
                  onChange={(e) => setMob(e.target.value)}
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Form.Label>
                  Punching ID <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={punchingid}
                  onChange={(e) => setPunchingID(e.target.value)}
                />
              </Col>

              {Role == "Cricket" && (
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    defaultValue={department}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setDepartment(e.target.value)
                    }
                  >
                    <option value={department}>{department}</option>
                    {allOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Designation</Form.Label>
                <Form.Select
                  defaultValue={designation}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setDesignation(e.target.value)
                  }
                >
                  <option value={designation}>{designation}</option>
                  <option value="Head Master">Head Master</option>
                  <option value="Principle">Principle</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Physical Teacher">Physical Teacher</option>
                </Form.Select>
              </Col>
            </Row>

            <hr className="my-1" />
            <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
              Role &amp; Assignment
            </h6>
            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Level</Form.Label>
                <Form.Select
                  defaultValue={level}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setLevel(e.target.value)
                  }
                >
                  <option value={level}>{level}</option>
                  {Level.filter((l) => l !== level).map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Role is set automatically from the level.
                </Form.Text>
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Region</Form.Label>
                <Form.Select
                  defaultValue={region}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setRegion(e.target.value)
                  }
                >
                  <option value={region}>
                    {regionName ||
                      allRegions?.find((r: any) => r._id === region)
                        ?.region_name ||
                      region}
                  </option>
                  {(allRegions || [])
                    .filter((r: any) => r._id !== region)
                    .map((r: any) => (
                      <option key={r._id} value={r._id}>
                        {r.region_name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            </Row>

            {level === "Admin" && (
              <>
                <hr className="my-1" />
                <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
                  Menu Access
                </h6>
                <div className="border rounded p-3 bg-light bg-opacity-50">
                  <Form.Check
                    type="checkbox"
                    id={`menu-access-all-${itemId}`}
                    label="Select all"
                    className="fw-semibold mb-2 pb-2 border-bottom"
                    checked={MENU_ACCESS_OPTIONS.every(
                      (option) => menuAccess[option.key]
                    )}
                    onChange={(e) =>
                      setMenuAccess(
                        e.target.checked
                          ? MENU_ACCESS_OPTIONS.reduce((acc, { key }) => {
                              acc[key] = true;
                              return acc;
                            }, {} as Record<string, boolean>)
                          : emptyMenuAccess()
                      )
                    }
                  />
                  <Row>
                    {MENU_ACCESS_OPTIONS.map((option) => (
                      <Col xs={6} md={4} key={option.key} className="mb-2">
                        <Form.Check
                          type="checkbox"
                          id={`menu-access-${itemId}-${option.key}`}
                          label={option.label}
                          checked={!!menuAccess[option.key]}
                          onChange={() => toggleMenuAccess(option.key)}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              </>
            )}

            {assignRegionType && (
              <>
                <hr className="my-1" />
                <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
                  Assign {assignRegionType === "district" ? "Districts" : "Blocks"}
                </h6>
                <Form.Select
                  multiple
                  value={assignRegion}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setAssignRegion(
                      Array.from(e.target.selectedOptions, (o) => o.value)
                    )
                  }
                  style={{ height: "180px" }}
                >
                  {assignRegionOptions.map((r: any) => (
                    <option key={r._id} value={r._id}>
                      {r.region_name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Hold Ctrl (Cmd on Mac) to select multiple. Leave empty to allow every {assignRegionType}.
                </Form.Text>
              </>
            )}

            <div className="d-flex justify-content-end gap-2 pt-4 mt-3 border-top">
              <Button variant="light" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </Form>
        ))}
    </>
  );
};
// _________________________________________________________

// const DeleteOriStruItem = ({id}) =>
const DeleteEmployee: React.FC<ParentId> = ({ id }) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [innerdata, setInnerData] = useState<OrgtnalSruItem[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [activeCategory, setActiveCategory] = useState<string>("admin");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetching, setIsFetching] = useState(false);

  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [viewItemId, setViewItemId] = useState<string | null>(null);
  const [isResponse, setIsResponse] = useState("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<OrgtnalStru[]>([]);

  const { user, userLoggedIn, loading, error, userId } = useSelector(
    (state: RootState) => ({
      user: state.Auth.user,
      loading: state.Auth.loading,
      error: state.Auth.error,
      userLoggedIn: state.Auth.userLoggedIn,
      userId: state.Auth.id,
    })
  );
  let profileId = user._id ? user._id : user.user ? user.user._id : "";
  const Key_Key = user.moniterd_ttl
    ? user.moniterd_ttl
    : user.user
      ? user.user.moniterd_ttl
      : "";
  let Departmentforfilter = user.department
    ? user.department
    : user.user.department;
  // --delete org str item
  const deleteItem = async (itemId: string) => {
    try {
      const response = await api.updatePatchDataDelete(
        `/api/delete/added/user/${profileId}/employee/${itemId}/${Key_Key}`,
        ""
      );
      if (response.status === 440) {
        alert("Session Expired");
        dispatch(logoutUser());
        window.location.href = ADMIN_LOGIN_BASE_URL; // Use the full URL, including the protocol (http or https)
      }
      if (!response.status == true) {
        // console.log("response--", await response.json())
        let errorMsg = await response.json();
        setIsResponse(errorMsg.message);
        // console.log("response from update user---",response)
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      if (
        response.status == 200 ||
        response.status == 201 ||
        response.status == 204
      ) {
        alert("Deleted");
        window.location.reload();
      }
    } catch (error) {
      // setIsResponse("Try after Some time")
      setShowModal(true);
      console.error("Error during delete the employee:", error);
    }
  };

  // Debounce the search box so every keystroke doesn't fire a request, and
  // jump back to page 1 once the term actually changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Switching tabs is a new filter, not a new page of the old one.
  useEffect(() => {
    setPage(1);
  }, [activeCategory]);

  const fetchUsers = useCallback(async () => {
    setIsFetching(true);
    try {
      const params = new URLSearchParams({
        category: activeCategory,
        page: String(page),
        limit: String(limit),
      });
      if (searchQuery) params.set("search", searchQuery);

      const response = await api.fetchData(
        `/get/all/registered/user?${params.toString()}`
      );
      if (response.status === 440) {
        alert("Session Expired");
        dispatch(logoutUser());
        window.location.href = ADMIN_LOGIN_BASE_URL; // Use the full URL, including the protocol (http or https)
        return;
      }
      if (!response.status == true) {
        let errorMsg = await response.json();
        setIsResponse(errorMsg.message);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setData(response.data?.sanitizedUsers || []);
      setTotal(response.data?.total ?? 0);
      setTotalPages(response.data?.totalPages ?? 1);
    } catch (error) {
      console.error("Error during fetch:", error);
    } finally {
      setIsFetching(false);
    }
  }, [activeCategory, page, limit, searchQuery, dispatch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // A short, centered window of page numbers instead of one item per page.
  const pageWindow = (() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const items: number[] = [];
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  })();
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  let Role = user.hobbies ? user.hobbies : user.user.hobbies;
  const canManage = Role === "Cricket";
  const viewUser = data.find((item) => item._id === viewItemId);

  return (
    <>
      <Card>
        <Card.Header className="bg-white">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <h4 className="mb-0">All Users</h4>
                  <Badge bg="light" text="dark">
                    {total} total
                  </Badge>
                </div>
                <Nav variant="tabs" className="border-bottom-0">
                  {USER_CATEGORIES.map((category) => (
                    <Nav.Item key={category.key}>
                      <Nav.Link
                        active={activeCategory === category.key}
                        onClick={() => setActiveCategory(category.key)}
                      >
                        {category.label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Card.Header>
              <Card.Body>
                {isResponse && (
                  <Alert variant="danger" className="my-2">
                    {isResponse}
                  </Alert>
                )}

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div className="input-group" style={{ maxWidth: 300 }}>
                    <span className="input-group-text bg-light border-end-0">
                      <FeatherIcon icon="search" size={14} />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search by name, email, punching ID..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                  <Form.Select
                    value={limit}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    className="w-auto"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </Form.Select>
                </div>

                {isFetching ? (
                  <div className="text-center py-5 text-muted">Loading...</div>
                ) : (
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0" hover>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Designation</th>
                        <th>Punching ID</th>
                        {canManage ? <th>Department</th> : ""}
                        {canManage ? <th className="text-end">Action</th> : ""}
                      </tr>
                    </thead>
                    <tbody>
                      {(data || []).length > 0 ? (
                        (data || []).map((item, i) => (
                          <tr key={item?._id}>
                            <td>
                              {canManage &&
                              item?.superDuperAdminApprovalStatus ===
                                "Pending" ? (
                                <Image
                                  src={img}
                                  width={20}
                                  height={20}
                                  alt="Pending approval"
                                  className="me-1"
                                />
                              ) : (
                                ""
                              )}
                              {(page - 1) * limit + i + 1}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <NameAvatar name={item?.name} />
                                <span>{item?.name}</span>
                              </div>
                            </td>
                            <td>{item?.email}</td>
                            <td>{item?.designation}</td>
                            <td>{item?.punchingid}</td>
                            {canManage ? <td>{item?.department}</td> : ""}
                            {canManage ? (
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  <button
                                    onClick={() => setViewItemId(item._id!)}
                                    className="btn btn-outline-info btn-sm"
                                  >
                                    View
                                  </button>
                                  <button
                                    onClick={() => setEditItemId(item?._id!)}
                                    className="btn btn-outline-primary btn-sm"
                                  >
                                    Update
                                  </button>
                                  <button
                                    onClick={() => deleteItem(item?._id!)}
                                    className="btn btn-outline-danger btn-sm"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            ) : (
                              ""
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={canManage ? 7 : 5}
                            className="text-center text-muted py-4"
                          >
                            No users found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                )}

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                  <span className="text-muted small">
                    {total === 0
                      ? "No users found"
                      : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
                  </span>
                  {totalPages > 1 && (
                    <Pagination className="mb-0">
                      <Pagination.First
                        disabled={page === 1}
                        onClick={() => setPage(1)}
                      />
                      <Pagination.Prev
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      />
                      {pageWindow.map((p) => (
                        <Pagination.Item
                          key={p}
                          active={p === page}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={page === totalPages}
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                      />
                      <Pagination.Last
                        disabled={page === totalPages}
                        onClick={() => setPage(totalPages)}
                      />
                    </Pagination>
                  )}
                </div>
              </Card.Body>
            </Card>

      <Modal
        show={!!editItemId}
        onHide={() => setEditItemId(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editItemId && data.length > 0 && (
            <UpdateEmployee
              itemId={editItemId}
              parentId={id}
              innerdata={data}
              onCancel={() => setEditItemId(null)}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!viewItemId}
        onHide={() => setViewItemId(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-6 text-uppercase text-muted fw-bold">
            User Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {viewUser && (
            <>
              <div className="d-flex align-items-center gap-3 p-3 mb-4 rounded-3 bg-light">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white fw-bold flex-shrink-0"
                  style={{ width: 56, height: 56, fontSize: 22 }}
                >
                  {(viewUser.name || "?").trim().charAt(0).toUpperCase()}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-semibold fs-5">
                      {viewUser.title} {viewUser.name}
                    </span>
                    {viewUser.level && (
                      <Badge bg="primary" pill>
                        {viewUser.level}
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted small">{viewUser.email}</div>
                </div>
              </div>

              <Row>
                <DetailField label="Mobile Number" value={viewUser.mob} />
                <DetailField label="Punching ID" value={viewUser.punchingid} />
                <DetailField label="Designation" value={viewUser.designation} />
                <DetailField label="Department" value={viewUser.department} />
                <DetailField
                  label="Region"
                  value={
                    getRegionName(viewUser.region) ||
                    viewUser.CRC_Name_Code ||
                    viewUser.Block_Name_Code ||
                    viewUser.districtCode ||
                    viewUser.stateName
                  }
                />
              </Row>

              <hr className="my-1" />
              <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
                Approval Status
              </h6>
              <Row className="g-2 mb-4">
                {[
                  { label: "Admin", status: viewUser.adminApprovalStatus },
                  {
                    label: "Super Admin",
                    status: viewUser.superAdminApprovalStatus,
                  },
                  {
                    label: "Super Duper Admin",
                    status: viewUser.superDuperAdminApprovalStatus,
                  },
                ].map((entry) => (
                  <Col xs={4} key={entry.label} className="text-center">
                    <div className="border rounded-3 py-2 h-100">
                      <div className="text-muted small mb-1">
                        {entry.label}
                      </div>
                      <ApprovalBadge status={entry.status} />
                    </div>
                  </Col>
                ))}
              </Row>

              {viewUser.level === "Admin" && (
                <>
                  <h6 className="text-uppercase text-muted fw-bold small mb-3">
                    Menu Access
                    <span className="text-lowercase fw-normal">
                      {" "}
                      (
                      {
                        MENU_ACCESS_OPTIONS.filter(
                          (option) => viewUser.menuAccess?.[option.key]
                        ).length
                      }
                      /{MENU_ACCESS_OPTIONS.length} granted)
                    </span>
                  </h6>
                  <div className="d-flex flex-wrap gap-2 mb-1">
                    {MENU_ACCESS_OPTIONS.filter(
                      (option) => viewUser.menuAccess?.[option.key]
                    ).map((option) => (
                      <Badge key={option.key} bg="success">
                        {option.label}
                      </Badge>
                    ))}
                    {MENU_ACCESS_OPTIONS.every(
                      (option) => !viewUser.menuAccess?.[option.key]
                    ) && (
                      <span className="text-muted small">
                        No menus granted
                      </span>
                    )}
                  </div>
                </>
              )}

              {(viewUser.createdBy || viewUser.createdAt) && (
                <div className="text-muted small mt-4 pt-3 border-top">
                  Added by {viewUser.createdBy || "—"}
                  {viewUser.createdAt &&
                    ` on ${new Date(viewUser.createdAt).toLocaleDateString(
                      undefined,
                      { year: "numeric", month: "short", day: "numeric" }
                    )}`}
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

// -------------------------------add org item----------------

// _______________________________________________________________

const Employees = () => {
  const { user, userLoggedIn, loading, error, _id } = useSelector(
    (state: RootState) => ({
      user: state.Auth.user,
      loading: state.Auth.loading,
      error: state.Auth.error,
      userLoggedIn: state.Auth.userLoggedIn,
      _id: state.Auth._id,
    })
  );
  let profileId = user._id ? user._id : user.user ? user.user._id : "";
  const Key_Key = user.moniterd_ttl
    ? user.moniterd_ttl
    : user.user
      ? user.user.moniterd_ttl
      : "";
  let Departmentforfilter = user.department
    ? user.department
    : user.user.department;
  let Role = user.hobbies ? user.hobbies : user.user.hobbies;
  let profileName = user.name ? user.name : user.user.name;
  console.log("user employee", user); //hobbies:"Cricket"
  const [allRegions, setAllRegions] = useState<any>();
  const [level, setLevel] = useState("");
  const [region, setRegion] = useState("");
  const [permission, setPermission] = useState("");
  const [menuAccess, setMenuAccess] = useState<Record<string, boolean>>(emptyMenuAccess());
  const [assignRegion, setAssignRegion] = useState<string[]>([]);
  const [data, setData] = useState<userData[]>([]);
  const [email, setEmail] = useState("");
  const [punchingid, setPunchingID] = useState("");
  const [name, setName] = useState("");
  const [mob, setMob] = useState("");

  const [password, setPassword] = useState("");
  const [designation, setDesignation] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [department, setDepartment] = useState(
    Role === "Cricket" ? "" : Departmentforfilter
  );

  // const [department, setDepartment] = useState("");
  // if (Role == 'Music') {
  //   setDepartment(Departmentforfilter)
  // }
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isResponse, setIsResponse] = useState("");
  const dispatch = useDispatch<AppDispatch>();

  const allOptions = ["Sports", "Physical Teacher"];

  const [filteredRegions, setFilteredRegions] = useState<any[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");


  // console.log(selectedLevel, "selectedLevel");

  const toggleMenuAccess = (key: string) =>
    setMenuAccess((prev) => ({ ...prev, [key]: !prev[key] }));

  // Menu Access only applies to Admin-level users - clear any selection made
  // before switching away from Admin so a hidden, stale grant can't be
  // submitted along with the form.
  useEffect(() => {
    if (selectedLevel !== "Admin") setMenuAccess(emptyMenuAccess());
  }, [selectedLevel]);

  // assignRegion only makes sense alongside districtLevel/blockLevel menu
  // access - clear any hidden, stale selection once neither is checked.
  useEffect(() => {
    if (!menuAccess.districtLevel && !menuAccess.blockLevel) {
      setAssignRegion([]);
    }
  }, [menuAccess.districtLevel, menuAccess.blockLevel]);

  const assignRegionType = menuAccess.districtLevel
    ? "district"
    : menuAccess.blockLevel
      ? "block"
      : null;
  const assignRegionOptions = (allRegions || []).filter(
    (r: any) => r.region_type === assignRegionType
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(); // append every thing one by one inside this form data
    formData.append("email", email);
    formData.append("punchingid", punchingid);
    formData.append("name", name);
    formData.append("password", password);
    formData.append("level", selectedLevel);
    formData.append("region", selectedRegion);
    formData.append("permission", permission);
    formData.append("menuAccess", JSON.stringify(menuAccess));
    formData.append("assignRegion", JSON.stringify(assignRegion));
    formData.append("mob", mob);
    formData.append("createdBy", profileName);
    formData.append("department", department);
    formData.append("designation", designation);

    if (image) {
      formData.append("image", image);
    }
    try {
      const response = await api.createData(
        `/adding/user/${profileId}/${Key_Key}`,
        formData
      );
      if (response.status === 440) {
        alert("Session Expired");
        dispatch(logoutUser());
        window.location.href = ADMIN_LOGIN_BASE_URL; // Use the full URL, including the protocol (http or https)
      }
      if (response.status === 400) {
        setIsError(true);
        let errorMessage = await response.message;
        setIsResponse(errorMessage);
      }
      if (!response.status == true) {
        let errorMsg = await response.json();
        setIsResponse(errorMsg.message);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      if (response.status == 200 || response.status == 201) {
        alert("Added");
        window.location.reload();
      }
    } catch (error: any) {
      console.error("Error during add employee:", error);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        let admin =
          Role == "Cricket"
            ? `/user/${profileId}/${Key_Key}`
            : `/user/${profileId}/${Key_Key}/${Departmentforfilter}`;
        const response = await api.fetchDataAuth(
          // `/user/${profileId}/${Key_Key}/${Departmentforfilter}`
          `${admin}`
        );
        // console.log(response, "response");

        if (response.status === 440) {
          alert("Session Expired");
          dispatch(logoutUser());
          window.location.href = ADMIN_LOGIN_BASE_URL; // Use the full URL, including the protocol (http or https)
        }
        if (response.status === 400) {
          setIsError(true);
          let errorMessage = await response.message;
          setIsResponse(errorMessage);
        }

        if (!response.status == true) {
          let errorMsg = await response.json();
          setIsResponse(errorMsg.message);
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const res = response.data;
        setData(res);
      } catch (error) {
        console.error("Error during fetch:", error);
      }
    };

    fetchData();
  }, [profileId, Key_Key]);

  //   ------------------------------------------ setting image in the input--------------------------------
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const Level = ["Cluster", "Block", "District", "State", "Admin"];
  const fetchAllRegions = async () => {
    try {
      const response = await api.fetchData("/api/regions");
      setAllRegions(response?.data?.regions);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAllRegions();
  }, []);

  const Permissions = [
    "CREATE_USER",
    "READ_USER",
    "UPDATE_USER",
    "DELETE_USER",
    "CREATE_ROLE",
    "READ_ROLE",
    "UPDATE_ROLE",
    "DELETE_ROLE",
    "ACCESS_BLOCK_DATA",
    "ACCESS_DISTRICT_DATA",
    "ACCESS_STATE_DATA",
  ];

  // Filter regions based on selected level and region_type
  useEffect(() => {
    if (Array.isArray(allRegions) && allRegions.length > 0) {
      if (selectedLevel) {
        const levelMap: { [key: string]: string } = {
          CRC: "Cluster Resource Center",
          Block: "block",
          District: "district",
          State: "state", // Assuming 'state' as the region_type for state level
          Admin: "state", // Admin-tier accounts sit at the same region granularity as State
        };

        const validLevel = levelMap[selectedLevel]?.toLowerCase();
        if (validLevel) {
          setFilteredRegions(
            allRegions.filter(
              (region: any) => region.region_type?.toLowerCase() === validLevel
            )
          );
        
        } else {
          console.error(`Invalid selectedLevel: ${selectedLevel}`);
        }
      } else {
        setFilteredRegions(allRegions); // If no level selected, show all regions
      }
    } else {
      console.warn('allRegions is empty or not an array');
    }
  }, [selectedLevel, allRegions]);

  const filteredOptions = filteredRegions.filter((region) =>
    region.region_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /*----------------  handle update----------------*/
  // console.log(data, "DATAAAAA");

  console.log("level---", level, selectedLevel)

  const canManageUsers = Role === "Cricket";

  return (
    <>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">Users</h3>
          {canManageUsers && (
            <Button onClick={() => setShowAddModal(true)}>
              <FeatherIcon icon="user-plus" size={16} className="me-1" />
              Add User
            </Button>
          )}
        </div>

        <Modal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {Role === "Cricket"
                ? "Create User"
                : `Create User in ${Departmentforfilter} Department`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(Role && Role === "Cricket") || Role === "Cricket" ? (
              <Form id="createUserForm" onSubmit={handleSubmit}>
                {isError && (
                  <Alert variant="danger" className="my-2">
                    {isResponse}
                  </Alert>
                )}
                {showModal && <AuthModal />}

                <h6 className="text-uppercase text-muted fw-bold small mb-3">
                  Basic Information
                </h6>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>
                      Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Name"
                      value={name}
                      required
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Col>

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>
                      Email address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter Email"
                      value={email}
                      autoComplete="off"
                      required
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Col>

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>
                      Mobile Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter Mobile Number"
                      value={mob}
                      pattern="[0-9]*"
                      title="Please enter only digits"
                      required
                      onChange={(e) => setMob(e.target.value)}
                    />
                  </Col>

                  {Role == "Cricket" && (
                    <Col xs={12} md={6} className="mb-3">
                      <Form.Label>
                        Password <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        autoComplete="off"
                        required
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </Col>
                  )}

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>
                      Punching ID <span className="text-muted">(optional)</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Punching ID"
                      value={punchingid}
                      onChange={(e) => setPunchingID(e.target.value)}
                    />
                  </Col>

                  {Role == "Cricket" && (
                    <Col xs={12} md={6} className="mb-3">
                      <Form.Label>Department</Form.Label>
                      <Form.Select
                        defaultValue=""
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setDepartment(e.target.value)
                        }
                      >
                        <option value="" disabled>
                          Select Department
                        </option>
                        {allOptions.map((option, index) => (
                          <option key={index} value={option}>
                            {option}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  )}

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Designation</Form.Label>
                    <Form.Select
                      defaultValue=""
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setDesignation(e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select Designation
                      </option>
                      <option value="Head Master">Head Master</option>
                      <option value="Principle">Principle</option>
                      <option value="Teacher">Teacher</option>
                      <option value="Physical Teacher">Physical Teacher</option>
                    </Form.Select>
                  </Col>
                </Row>

                <hr className="my-1" />
                <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
                  Role &amp; Assignment
                </h6>
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Level</Form.Label>
                    <Form.Select
                      defaultValue=""
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setSelectedLevel(e.target.value)
                      }
                    >
                      <option value="" disabled>
                        Select Level
                      </option>
                      {Level &&
                        Level.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Role is set automatically from the level.
                    </Form.Text>
                  </Col>

                  <Col xs={12} md={6} className="mb-3">
                    <Form.Label>Region</Form.Label>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        id="dropdown-basic"
                        className="w-100 text-truncate"
                        style={{ textAlign: "left" }}
                      >
                        {selectedRegion
                          ? filteredRegions.find((r) => r._id === selectedRegion)
                              ?.region_name
                          : "Select Region"}
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        className="w-100"
                        style={{ maxHeight: "300px", overflowY: "auto" }}
                      >
                        <div className="px-2 pb-2">
                          <Form.Control
                            type="text"
                            placeholder="Search for a region"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>

                        {filteredOptions.length > 0 ? (
                          filteredOptions.map((region) => (
                            <Dropdown.Item
                              key={region._id}
                              onClick={() => setSelectedRegion(region._id)}
                            >
                              {region.region_name}
                            </Dropdown.Item>
                          ))
                        ) : (
                          <Dropdown.Item disabled>No regions available</Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </Row>

                {selectedLevel === "Admin" && (
                  <>
                    <hr className="my-1" />
                    <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
                      Menu Access
                    </h6>
                    <div className="border rounded p-3 bg-light bg-opacity-50">
                      <Form.Check
                        type="checkbox"
                        id="create-menu-access-all"
                        label="Select all"
                        className="fw-semibold mb-2 pb-2 border-bottom"
                        checked={MENU_ACCESS_OPTIONS.every(
                          (option) => menuAccess[option.key]
                        )}
                        onChange={(e) =>
                          setMenuAccess(
                            e.target.checked
                              ? MENU_ACCESS_OPTIONS.reduce((acc, { key }) => {
                                  acc[key] = true;
                                  return acc;
                                }, {} as Record<string, boolean>)
                              : emptyMenuAccess()
                          )
                        }
                      />
                      <Row>
                        {MENU_ACCESS_OPTIONS.map((option) => (
                          <Col xs={6} md={4} key={option.key} className="mb-2">
                            <Form.Check
                              type="checkbox"
                              id={`create-menu-access-${option.key}`}
                              label={option.label}
                              checked={!!menuAccess[option.key]}
                              onChange={() => toggleMenuAccess(option.key)}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </>
                )}

                {assignRegionType && (
                  <>
                    <hr className="my-1" />
                    <h6 className="text-uppercase text-muted fw-bold small mb-3 mt-3">
                      Assign {assignRegionType === "district" ? "Districts" : "Blocks"}
                    </h6>
                    <Form.Select
                      multiple
                      value={assignRegion}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setAssignRegion(
                          Array.from(e.target.selectedOptions, (o) => o.value)
                        )
                      }
                      style={{ height: "180px" }}
                    >
                      {assignRegionOptions.map((r: any) => (
                        <option key={r._id} value={r._id}>
                          {r.region_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Hold Ctrl (Cmd on Mac) to select multiple. Leave empty to allow every {assignRegionType}.
                    </Form.Text>
                  </>
                )}
              </Form>
            ) : (
              ""
            )}
          </Modal.Body>
          {((Role && Role === "Cricket") || Role === "Cricket") && (
            <Modal.Footer>
              <Button
                variant="light"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" form="createUserForm">
                Create User
              </Button>
            </Modal.Footer>
          )}
        </Modal>

        {canManageUsers ? (
          <Row>
            <Col lg={12}>{user && <DeleteEmployee id={user.id} />}</Col>
          </Row>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <h3>You have no access to create a new User</h3>
          </div>
        )}
      </div>
    </>
  );
};

export default Employees;
